import { Link } from "react-router-dom"

import Button from "components/ui/button"
import Divider from "components/ui/divider"
import { IS_US, IS_NA, jewlr_t } from "helpers/application"

import Accordion from "./accordion"

const warrantyDetails = () => [
  {
    heading: `${jewlr_t("terms.Jewelry")}-Specific Warranty Details`,
    title: "Rings",
    why: [
      {
        importantNotes: (
          <>
            <p className="body mb-16">
              Rings can be resized within two full sizes. Additional sizing
              requires a remake at <b>30% off</b> the current price.
            </p>
            <br />
            <p className="body">
              The tarnishing of silver or plated {jewlr_t("terms.jewelry")} is a
              natural process, and you can restore its shine at home using a
              cloth. Please click{" "}
              <Button asChild underline>
                <Link
                  to={`/helpdesk/a/603/how-do-i-clean-my-${jewlr_t(
                    "terms.jewelry"
                  )}`}
                >
                  here
                </Link>
              </Button>{" "}
              for more information.
            </p>
          </>
        ),
        whatIsCovered:
          "Loose or poorly set gemstones; misaligned prongs; metal cracks",
        whatsNotCovered: "Bent prongs; stone and metal loss from wear and tear",
      },
    ],
  },
  {
    title: "Necklaces/Pendants",
    why: [
      {
        importantNotes: (
          <p className="body">
            The tarnishing of silver or plated {jewlr_t("terms.jewelry")} is a
            natural process, and you can restore its shine at home using a soft
            cloth. Please click{" "}
            <Button asChild underline>
              <Link
                to={`/helpdesk/a/603/how-do-i-clean-my-${jewlr_t(
                  "terms.jewelry"
                )}`}
              >
                here
              </Link>
            </Button>{" "}
            for more information.
          </p>
        ),
        whatIsCovered:
          "Clasp failures; weak chain links; loose pendants; loose or poorly set gemstones",
        whatsNotCovered:
          "Chain tangling; misuse-related breakage; bent prongs; stone and metal loss from wear and tear",
      },
    ],
  },
  {
    title: "Bracelets",
    why: [
      {
        importantNotes: (
          <p className="body">
            The tarnishing of silver or plated {jewlr_t("terms.jewelry")} is a
            natural process, and you can restore its shine at home using a soft
            cloth. Please click{" "}
            <Button asChild underline>
              <Link
                to={`/helpdesk/a/603/how-do-i-clean-my-${jewlr_t(
                  "terms.jewelry"
                )}`}
              >
                here
              </Link>
            </Button>{" "}
            for more information.
          </p>
        ),
        whatIsCovered: "Weak clasps; loose or poorly set gemstones",
        whatsNotCovered:
          "Chain stretching; misuse-related breakage; bent prongs; stone and metal loss from wear and tear",
      },
    ],
  },
  {
    title: "Earrings",
    why: [
      {
        importantNotes: (
          <p className="body">
            The tarnishing of silver or plated {jewlr_t("terms.jewelry")} is a
            natural process, and you can restore its shine at home using a soft
            cloth. Please click{" "}
            <Button asChild underline>
              <Link
                to={`/helpdesk/a/603/how-do-i-clean-my-${jewlr_t(
                  "terms.jewelry"
                )}`}
              >
                here
              </Link>
            </Button>{" "}
            for more information.
          </p>
        ),
        whatIsCovered:
          "Loose posts, backs, hooks; loose or poorly set gemstones",
        whatsNotCovered:
          "Bent posts; loss from improper fastening; misuse-related breakage; bent prongs; stone and metal loss from wear and tear",
      },
    ],
  },
  {
    title: jewlr_t("terms.customization").toUpperCase(),
    why: [
      {
        importantNotes: (
          <p className="body">
            The tarnishing of silver or plated {jewlr_t("terms.jewelry")} is a
            natural process, and you can restore its shine at home using a soft
            cloth. Please click{" "}
            <Button asChild underline>
              <Link
                to={`/helpdesk/a/603/how-do-i-clean-my-${jewlr_t(
                  "terms.jewelry"
                )}`}
              >
                here
              </Link>
            </Button>{" "}
            for more information.
          </p>
        ),
        whatIsCovered: "Incorrect engraving",
        whatsNotCovered: `Sizing/${jewlr_t(
          "terms.personalization"
        )} errors by the customer`,
      },
    ],
  },
]

const warrantyMaterials = () => [
  {
    title: IS_NA() ? "Vermeil" : "Gold Vermeil",
    why: [
      {
        content: (
          <>
            Covered for <b>one year</b>. Tarnishing or wear is not covered as
            it&apos;s a natural result of regular use.
          </>
        ),
      },
    ],
  },
  {
    title: "Sterling Silver",
    why: [
      {
        content: (
          <>
            Tarnishing is a natural process and is not covered. We&apos;re happy
            to offer tips on keeping your silver pieces looking their best.
            Please click{" "}
            <Button asChild underline>
              <Link
                to={`/helpdesk/a/768/my-sterling-silver-${jewlr_t(
                  "terms.jewelry"
                )}-is-tarnishing-what-should-i-do`}
              >
                here
              </Link>
            </Button>{" "}
            for more information.
          </>
        ),
      },
    ],
  },
  {
    title: "Gold Plating",
    why: [
      {
        content: (
          <>
            Tarnishing or wear of the plating over time is normal and not
            covered. Please click{" "}
            <Button asChild underline>
              <Link to={`/blog/top-tips-for-${jewlr_t("terms.jewelry")}-care`}>
                here
              </Link>
            </Button>{" "}
            for information on caring for your {jewlr_t("terms.jewelry")}.
          </>
        ),
      },
    ],
  },
  {
    title: "Tungsten",
    why: [
      {
        content: (
          <>
            Tungsten is durable and made to last, but it cannot be re-plated,
            re-engraved, resized, or repaired. If something happens within{" "}
            <b>one year</b>, we&apos;ll replace it.
          </>
        ),
      },
    ],
  },
]

const WarrantyDetails = () => {
  const warrantyDetailsData = warrantyDetails()
  const warrantyMaterialsData = warrantyMaterials()

  return (
    <div
      className="max-w-1520 desktop:mb-60 mx-auto mb-32"
      id="warranty-details"
    >
      <h2 className="h2 desktop:mb-32 mb-24">
        {IS_US()
          ? `${jewlr_t("Jewelry")}-Specific Warranty Details`
          : `One-Year ${jewlr_t("Jewelry")} Warranty Details`}
      </h2>
      <Divider className="bg-black" />
      {warrantyDetailsData.map((data, i) => (
        <Accordion data={data} key={`why${i}`} />
      ))}
      <h2 className="h2 tablet:mb-32 desktop:mt-120 mb-16 mt-32">
        Special Warranty for Materials
      </h2>
      <Divider className="bg-black" />
      {warrantyMaterialsData.map((data, i) => (
        <Accordion data={data} key={`why${i}`} />
      ))}
    </div>
  )
}

export default WarrantyDetails
